import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAdministrationAPI } from "../AdministrationAPI";
import { useModalContext } from "../components/ModalProvider";
import { useCustomerTheme } from "../Theme";
import Section from "../components/Section";
import Metric from "../components/Metric";

import ReactApexChart from "react-apexcharts";
import dayjs from "dayjs";

import { RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export default function AdministrationMarketplaceStatisticsPanel() {
  const { t } = useTranslation();
  const { pushError } = useModalContext() as any;
  const { theme } = useCustomerTheme();
  const { getAdministrationMarketplaceStatistics } = useAdministrationAPI();

  const [statistics, setStatistics] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const stats = await getAdministrationMarketplaceStatistics();
      setStatistics(stats);
    } catch (error: any) {
      const is404 = error?.message?.includes("Not Found");
      if (!is404) {
        console.error("Failed to fetch administration marketplace statistics:", error);
        pushError(error);
      }
      setStatistics(null);
    } finally {
      setLoading(false);
    }
  }, [getAdministrationMarketplaceStatistics, pushError]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Auto-refresh every 15 minutes
  useEffect(() => {
    const interval = setInterval(refresh, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, [refresh]);

  if (loading && !statistics) {
    return (
      <div
        className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4"
        role="status"
        aria-live="polite"
      >
        <span className="sr-only">{t("global.loading")}</span>
        <Skeleton className="h-7 w-[240px]" />
        <div className="grid min-w-0 grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-4">
          <Skeleton className="h-[248px] w-full" />
          <Skeleton className="h-[248px] w-full" />
          <Skeleton className="h-[248px] w-full" />
        </div>
        <Skeleton className="h-[356px] w-full" />
      </div>
    );
  }

  return (
    <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <h2 className="min-w-0 flex-1 text-lg font-semibold">
          {t("administrationPage.marketplaceStatistics.title")}
        </h2>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t("statisticsPanel.marketplaceRefresh")}
                onClick={refresh}
                disabled={loading}
              >
                <RefreshCw
                  aria-hidden
                  className={loading ? "h-4 w-4 animate-spin" : "h-4 w-4"}
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("statisticsPanel.marketplaceRefresh")}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {statistics ? (
        <div className="flex min-w-0 flex-col gap-4">
          <div className="grid min-w-0 grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-4">
            <Section title="">
              <div className="flex flex-col gap-4">
                <Metric
                  size="large"
                  title={t("administrationPage.marketplaceStatistics.firstPageVisitsToday")}
                  value={String(statistics.summary?.firstPageVisitsToday ?? statistics.firstPageVisitsToday ?? 0)}
                  color={theme.secondary}
                />
                <Metric
                  size="large"
                  title={t("administrationPage.marketplaceStatistics.productSelectionToday")}
                  value={String(statistics.summary?.productSelectionToday ?? 0)}
                  color={theme.tertiary}
                />
              </div>
            </Section>
            <Section title="">
              <div className="flex flex-col gap-4">
                <Metric
                  size="large"
                  title={t("administrationPage.marketplaceStatistics.firstPageVisitsThisMonth")}
                  value={String(statistics.summary?.firstPageVisitsThisMonth ?? statistics.firstPageVisitsThisMonth ?? 0)}
                  color={theme.secondary}
                />
                <Metric
                  size="large"
                  title={t("administrationPage.marketplaceStatistics.productSelectionThisMonth")}
                  value={String(statistics.summary?.productSelectionThisMonth ?? 0)}
                  color={theme.tertiary}
                />
              </div>
            </Section>
            {statistics.summary?.conversionRateThisMonth != null && (
              <Section title="">
                <Metric
                  size="large"
                  title={t("administrationPage.marketplaceStatistics.conversionRateThisMonth")}
                  value={`${statistics.summary.conversionRateThisMonth.toFixed(1)}%`}
                  color={theme.primary}
                />
              </Section>
            )}
          </div>

          {statistics.firstPageVisitsOverTime?.length > 0 && (
            <Section title={t("administrationPage.marketplaceStatistics.firstPageVisitsOverTime")}>
              <div className="min-w-0">
                <ReactApexChart
                  options={{
                    chart: { type: "area" },
                    xaxis: {
                      categories: statistics.firstPageVisitsOverTime.map((d: any) =>
                        dayjs(d.date).format(t("global.dateShort"))
                      ),
                    },
                    yaxis: { title: { text: t("administrationPage.marketplaceStatistics.firstPageVisits") } },
                    colors: [theme.secondary],
                    dataLabels: { enabled: false },
                    stroke: { curve: "smooth" },
                  } as any}
                  series={[
                    {
                      name: t("administrationPage.marketplaceStatistics.firstPageVisits"),
                      data: statistics.firstPageVisitsOverTime.map((d: any) => d.count ?? 0),
                    },
                  ]}
                  type="area"
                  height={300}
                />
              </div>
            </Section>
          )}

          {statistics.productSelectionOverTime?.length > 0 && (
            <Section title={t("administrationPage.marketplaceStatistics.productSelectionOverTime")}>
              <div className="min-w-0">
                <ReactApexChart
                  options={{
                    chart: { type: "area" },
                    xaxis: {
                      categories: statistics.productSelectionOverTime.map((d: any) =>
                        dayjs(d.date).format(t("global.dateShort"))
                      ),
                    },
                    yaxis: { title: { text: t("administrationPage.marketplaceStatistics.productSelection") } },
                    colors: [theme.tertiary],
                    dataLabels: { enabled: false },
                    stroke: { curve: "smooth" },
                  } as any}
                  series={[
                    {
                      name: t("administrationPage.marketplaceStatistics.productSelection"),
                      data: statistics.productSelectionOverTime.map((d: any) => d.count ?? 0),
                    },
                  ]}
                  type="area"
                  height={300}
                />
              </div>
            </Section>
          )}

          {statistics.funnel?.steps?.length > 0 && (
            <Section title={t("administrationPage.marketplaceStatistics.funnel")}>
              <div className="min-w-0">
                <ReactApexChart
                  options={{
                    chart: { type: "bar" },
                    plotOptions: {
                      bar: { horizontal: true, columnWidth: "60%", borderRadius: 4 },
                    },
                    xaxis: {
                      categories: statistics.funnel.steps.map((s: any) => s.name),
                    },
                    colors: [theme.primary, theme.secondary, theme.tertiary],
                    dataLabels: { enabled: true },
                  } as any}
                  series={[
                    {
                      name: t("statisticsPanel.marketplaceCount"),
                      data: statistics.funnel.steps.map((s: any) => s.count ?? 0),
                    },
                  ]}
                  type="bar"
                  height={Math.max(150, statistics.funnel.steps.length * 60)}
                />
              </div>
            </Section>
          )}

          {!statistics.summary &&
            statistics.firstPageVisitsToday == null &&
            statistics.firstPageVisitsThisMonth == null &&
            !statistics.firstPageVisitsOverTime?.length &&
            !statistics.productSelectionOverTime?.length &&
            !statistics.funnel?.steps?.length && (
              <EmptyState t={t} onRefresh={refresh} loading={loading} />
            )}
        </div>
      ) : (
        <EmptyState t={t} onRefresh={refresh} loading={loading} />
      )}
    </div>
  );
}

/** Leerzustand: Überschrift, Erklärung und Handlungsaufforderung. */
function EmptyState({
  t,
  onRefresh,
  loading,
}: {
  t: (key: string) => string;
  onRefresh: () => void;
  loading: boolean;
}) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-md border border-dashed py-8 text-center">
      <div className="text-sm font-semibold text-foreground">
        {t("administrationPage.marketplaceStatistics.noData")}
      </div>
      <div className="max-w-prose text-sm text-muted-foreground">
        {t("administrationPage.marketplaceStatistics.noDataHint")}
      </div>
      <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading}>
        {t("statisticsPanel.marketplaceRefresh")}
      </Button>
    </div>
  );
}
